/**
 * HUD design tokens + stylesheet (LOCKED-DESIGN 18.5 "restyled through a
 * design-token stylesheet shared with the shell"). This replaces the inline
 * cssText blobs that previously lived in Hud.ts: every color is sourced from
 * theme.ts (the single code-owned palette, Section 18.8) and every rule is a
 * token-driven class. The stylesheet is injected once per document.
 *
 * The shell (src/shell/style.css) owns the overlay/menu chrome; this owns the
 * in-arena HUD. Both draw from the same signal-color taxonomy so a teal here
 * and a teal there mean the same thing.
 */
import { CHAIN_TIER_COLORS, cssHex, FEEDBACK_COLORS, OVERFLOW_BUCKET_COLORS, PALETTE, RECEIVER_TIER_COLORS } from "./theme.js";

export const HUD_STYLE_ID = "tpz-hud-styles";

/** Per-Chain-tier ladder accents (×1..×3), exposed for pip tinting in Hud.ts. */
export const CHAIN_TIER_HEX = CHAIN_TIER_COLORS.map((color) => cssHex(color));
/** Receiver-tier accents for route/ticket chips (safe/technical/hazard). */
export const RECEIVER_TIER_HEX = {
  safe: cssHex(RECEIVER_TIER_COLORS.safe),
  technical: cssHex(RECEIVER_TIER_COLORS.technical),
  hazard: cssHex(RECEIVER_TIER_COLORS.hazard),
} as const;
/** Overflow location-bucket accents (Section 7 breakdown, LOCKED-DESIGN 18.10). */
export const OVERFLOW_BUCKET_HEX = {
  floor: cssHex(OVERFLOW_BUCKET_COLORS.floor),
  beam: cssHex(OVERFLOW_BUCKET_COLORS.beam),
  transit: cssHex(OVERFLOW_BUCKET_COLORS.transit),
  chamber: cssHex(OVERFLOW_BUCKET_COLORS.chamber),
  belt: cssHex(OVERFLOW_BUCKET_COLORS.belt),
} as const;

const TEAL = cssHex(PALETTE.conveyance);
const AMBER = cssHex(FEEDBACK_COLORS.overflowWarning);
const RED = cssHex(PALETTE.tierHazard);
const TEXT_DIM = cssHex(FEEDBACK_COLORS.muted);
const ANCHOR_BLUE = cssHex(PALETTE.anchorBlue);

/** The full HUD stylesheet, built from theme tokens. */
export function hudStylesheet(): string {
  return `
.tpz-hud {
  --tpz-teal: ${TEAL};
  --tpz-amber: ${AMBER};
  --tpz-red: ${RED};
  --tpz-text-hi: #eef2f8;
  --tpz-text-mid: #c7d0e0;
  --tpz-text-dim: ${TEXT_DIM};
  --tpz-panel: rgba(8, 11, 17, 0.82);
  --tpz-panel-solid: rgba(6, 9, 14, 0.94);
  --tpz-border: rgba(126, 148, 178, 0.20);
  --tpz-track: #212a38;
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  user-select: none;
  font: 13px/1.5 ui-monospace, "SF Mono", Menlo, monospace;
  color: var(--tpz-text-mid);
  text-shadow: 0 1px 3px #000;
}
.tpz-hud .tpz-el { position: absolute; }
.tpz-hud .tpz-panel-box {
  background: var(--tpz-panel);
  border: 1px solid var(--tpz-border);
  border-radius: 8px;
  padding: 6px 9px;
  backdrop-filter: blur(3px);
}
.tpz-hud .tpz-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--tpz-text-dim);
  text-transform: uppercase;
}

/* --- score + timer (central, real typographic weight) --- */
.tpz-timer {
  top: 8px; left: 50%; transform: translateX(-50%);
  font-size: 22px; font-weight: 800; letter-spacing: 3px; line-height: 1; color: var(--tpz-text-hi);
  font-variant-numeric: tabular-nums;
}
.tpz-timer.tpz-urgent { color: var(--tpz-red); }
.tpz-score {
  top: 34px; left: 50%; transform: translateX(-50%);
  text-align: center;
}
.tpz-score-value {
  display: block;
  font-size: 46px; font-weight: 800; letter-spacing: 1px; line-height: 1;
  color: var(--tpz-text-hi);
  font-variant-numeric: tabular-nums;
  transform-origin: 50% 50%;
  will-change: transform;
}
.tpz-score.tpz-anim .tpz-score-value { transition: transform 0.26s cubic-bezier(0.2, 0.7, 0.3, 1); }

/* --- grade meter (0-100 with C/B/A/S band + multiplier) --- */
.tpz-grade { top: 12px; left: 14px; width: 196px; }
.tpz-meter-head { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; margin-bottom: 4px; line-height: 1.1; }
.tpz-grade-band { font-size: 15px; font-weight: 800; letter-spacing: 0.5px; line-height: 1.1; }
.tpz-grade-mult { font-size: 11px; color: var(--tpz-text-dim); font-variant-numeric: tabular-nums; }
.tpz-grade-val { font-size: 12px; font-weight: 700; color: var(--tpz-text-mid); font-variant-numeric: tabular-nums; }
/* Grade-delta attribution chip (Section 8.3 causes, LOCKED-DESIGN 18.10):
   transient "+6 VARIED" beside the meter value; single element, latest wins */
.tpz-grade-delta { font-size: 11px; font-weight: 800; letter-spacing: 0.04em; font-variant-numeric: tabular-nums; }
.tpz-track {
  position: relative; height: 7px; border-radius: 4px;
  background: var(--tpz-track); overflow: hidden;
}
.tpz-fill { position: absolute; inset: 0 auto 0 0; width: 0%; border-radius: 4px; background: var(--tpz-teal); }
.tpz-hud:not(.tpz-reduced) .tpz-fill { transition: width 0.3s ease, background-color 0.3s ease; }

/* --- chain ladder (five tiers + 10s timer) --- */
.tpz-chain { top: 66px; left: 14px; width: 200px; }
.tpz-chain-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; line-height: 1.1; }
.tpz-chain-mult { font-size: 14px; font-weight: 800; color: var(--tpz-text-hi); font-variant-numeric: tabular-nums; line-height: 1.1; }
.tpz-chain-pips { display: flex; gap: 4px; margin-bottom: 4px; }
.tpz-pip { flex: 1; height: 6px; border-radius: 3px; background: var(--tpz-track); }
.tpz-hud:not(.tpz-reduced) .tpz-pip { transition: background-color 0.2s ease, box-shadow 0.2s ease; }
.tpz-chain-timer { height: 3px; }

/* --- dispatch panel (top-priority) --- */
.tpz-dispatch { top: 118px; left: 14px; width: 268px; }
.tpz-dispatch-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
.tpz-dispatch-title { font-size: 11px; font-weight: 800; letter-spacing: 0.12em; color: var(--tpz-amber); }
.tpz-dispatch-timer { font-size: 12px; font-weight: 700; color: var(--tpz-text-hi); font-variant-numeric: tabular-nums; }
.tpz-recipe-row { display: flex; align-items: center; gap: 7px; margin: 3px 0; }
.tpz-recipe-label { flex: 0 0 66px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.tpz-recipe-track { flex: 1; height: 6px; }
.tpz-recipe-count { flex: 0 0 34px; text-align: right; font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; }
.tpz-recipe-row.tpz-done .tpz-recipe-count { color: var(--tpz-teal); }
.tpz-recipe-row.tpz-done .tpz-recipe-count::after { content: " \\2713"; }
.tpz-dispatch-route { font-size: 11px; margin-top: 5px; color: var(--tpz-text-mid); }
.tpz-dispatch-route .tpz-chip { font-weight: 800; }
.tpz-dispatch-next {
  margin-top: 7px; padding-top: 6px; border-top: 1px dashed var(--tpz-border);
  font-size: 10.5px; color: var(--tpz-text-dim); line-height: 1.35;
}
.tpz-dispatch-next b { color: var(--tpz-text-mid); font-weight: 700; }

/* --- shift status + telegraph (right column) --- */
.tpz-shift { top: 54px; right: 14px; width: 300px; text-align: right; }
.tpz-shift-phase { font-size: 12.5px; font-weight: 800; color: var(--tpz-text-hi); letter-spacing: 0.02em; line-height: 1.2; white-space: nowrap; }
.tpz-shift-next { font-size: 11px; color: var(--tpz-text-dim); margin-top: 2px; line-height: 1.2; font-variant-numeric: tabular-nums; white-space: nowrap; }
.tpz-telegraph {
  top: 96px; right: 14px; width: 268px; text-align: right;
  border-color: var(--tpz-amber); border-left: none; border-right: 3px solid var(--tpz-amber);
}
.tpz-telegraph.tpz-imminent { border-right-color: var(--tpz-red); }
.tpz-telegraph-head { font-size: 12px; font-weight: 800; letter-spacing: 0.08em; color: var(--tpz-amber); }
.tpz-telegraph.tpz-imminent .tpz-telegraph-head { color: var(--tpz-red); }
.tpz-telegraph-change { font-size: 11px; color: var(--tpz-text-mid); margin-top: 2px; line-height: 1.35; }

/* --- baffle gate readout (hazard timing) --- */
.tpz-baffle { top: 184px; right: 14px; width: 232px; text-align: right; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; }

/* --- overflow edge-pressure meter --- */
.tpz-overflow { right: 14px; bottom: 14px; width: 214px; }
.tpz-overflow-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }
.tpz-overflow-label { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; }
.tpz-overflow-count { font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
.tpz-overflow-track { position: relative; height: 12px; }
.tpz-tick { position: absolute; top: -2px; bottom: -2px; width: 2px; background: rgba(0, 0, 0, 0.55); }
.tpz-overflow.tpz-warn .tpz-fill { background: var(--tpz-amber); }
.tpz-overflow.tpz-severe .tpz-fill { background: var(--tpz-red); }
.tpz-overflow-icon::before { content: "\\25B2 "; }
/* location breakdown (Section 7 buckets, LOCKED-DESIGN 18.10): a slim
   segmented strip + a one-line "where" readout under the pressure track,
   so 36/36 with a clean floor is never a mystery */
.tpz-overflow-buckets { position: relative; height: 5px; margin-top: 4px; border-radius: 3px; background: var(--tpz-track); overflow: hidden; }
.tpz-seg { position: absolute; top: 0; bottom: 0; }
.tpz-hud:not(.tpz-reduced) .tpz-seg { transition: left 0.3s ease, width 0.3s ease; }
.tpz-overflow-where {
  margin-top: 3px; font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
  color: var(--tpz-text-dim); line-height: 1.35; white-space: normal;
}
.tpz-overflow-where span { white-space: nowrap; }

/* --- reticle dock (bottom-center, six locked fields) --- */
.tpz-dock {
  left: 50%; bottom: 52px; transform: translateX(-50%);
  display: flex; gap: 14px; align-items: stretch;
  padding: 8px 14px;
}
.tpz-dock-field { display: flex; flex-direction: column; gap: 2px; min-width: 46px; }
.tpz-dock-label { font-size: 9px; font-weight: 700; letter-spacing: 0.1em; color: var(--tpz-text-dim); text-transform: uppercase; }
.tpz-dock-value { font-size: 14px; font-weight: 800; color: var(--tpz-text-hi); font-variant-numeric: tabular-nums; white-space: nowrap; }
.tpz-dock-field.tpz-fuse .tpz-dock-value { color: var(--tpz-red); }
.tpz-dock-sep { width: 1px; background: var(--tpz-border); }
/* Section 8.3 route-freshness state riding the ROUTE cell (18.10): icon +
   word + Grade delta; color is set inline from the freshness taxonomy */
.tpz-route-state { font-weight: 800; letter-spacing: 0.03em; }

/* --- jam-recovery prompt echo (reticle-dock area, LOCKED-DESIGN 18.10) --- */
.tpz-recovery {
  left: 50%; bottom: 112px; transform: translateX(-50%);
  max-width: min(560px, 82vw); text-align: center;
  padding: 8px 16px; border-radius: 7px;
  background: var(--tpz-panel-solid); border: 1px solid var(--tpz-amber);
  color: var(--tpz-text-hi);
  font-size: 13.5px; font-weight: 800; letter-spacing: 0.04em; line-height: 1.35;
}
/* second line: the interlock consequence (18.16) — quieter, never the headline */
.tpz-recovery-detail {
  margin-top: 4px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  color: var(--tpz-text-dim); opacity: 0.92;
}
.tpz-recovery.tpz-urgent { border-color: var(--tpz-red); color: #fff; box-shadow: 0 0 16px var(--tpz-red); }
.tpz-hud:not(.tpz-reduced) .tpz-recovery.tpz-urgent { animation: tpz-recovery-pulse 0.9s ease-in-out infinite; }
@keyframes tpz-recovery-pulse {
  0%, 100% { box-shadow: 0 0 10px var(--tpz-red); }
  50% { box-shadow: 0 0 22px var(--tpz-red); }
}

/* --- furnace-overload chip (LOCKED-DESIGN 18.12): rides above the recovery prompt --- */
.tpz-overload {
  left: 50%; bottom: 158px; transform: translateX(-50%);
  padding: 5px 14px; border-radius: 6px; text-align: center;
  background: var(--tpz-panel-solid); border: 1px solid var(--tpz-amber);
  color: var(--tpz-amber); font-size: 13px; font-weight: 900; letter-spacing: 0.06em;
}
.tpz-overload.tpz-held { border-color: var(--tpz-red); color: #fff; background: color-mix(in srgb, var(--tpz-red) 30%, var(--tpz-panel-solid)); }
.tpz-hud:not(.tpz-reduced) .tpz-overload.tpz-held { animation: tpz-recovery-pulse 0.7s ease-in-out infinite; }

/* --- CHAMBER HEAT dwell meter (LOCKED-DESIGN 18.12): vertical bar beside reticle --- */
.tpz-heat {
  left: calc(50% + 190px); bottom: 132px; width: 74px; text-align: center;
}
.tpz-heat-label { font-size: 10.5px; font-weight: 900; letter-spacing: 0.05em; color: var(--tpz-teal); margin-bottom: 4px; }
.tpz-heat-track {
  position: relative; width: 18px; height: 128px; margin: 0 auto;
  border: 1px solid var(--tpz-border); border-radius: 4px; overflow: hidden;
  background: color-mix(in srgb, var(--tpz-panel-solid) 80%, transparent);
}
.tpz-heat-fill {
  position: absolute; left: 0; right: 0; bottom: 0; height: 0%;
  background: linear-gradient(to top, var(--tpz-teal), var(--tpz-amber));
  transition: height 0.08s linear;
}
.tpz-heat-sec { font-size: 12px; font-weight: 800; color: var(--tpz-text-hi); margin-top: 4px; }
.tpz-heat.tpz-warning .tpz-heat-label { color: var(--tpz-red); }
.tpz-heat.tpz-warning .tpz-heat-fill { background: var(--tpz-red); }
.tpz-hud:not(.tpz-reduced) .tpz-heat.tpz-warning:not(.tpz-steady) .tpz-heat-label { animation: tpz-recovery-pulse 0.5s steps(2, end) infinite; }

/* --- central alert (critical overflow) --- */
.tpz-alert {
  top: 16%; left: 50%; transform: translateX(-50%); text-align: center;
  font-size: 21px; font-weight: 900; letter-spacing: 0.08em; color: var(--tpz-red);
  display: none;
}

/* --- transient center toast (coach voice) --- */
.tpz-toast {
  top: 27%; left: 50%; transform: translateX(-50%); display: none;
  padding: 7px 14px; border-radius: 6px; background: var(--tpz-panel-solid);
  font-size: 15px; font-weight: 700; letter-spacing: 0.06em; text-align: center;
  border: 1px solid var(--tpz-border);
}

/* --- off-screen event indicator (edge arrow toward off-camera events) --- */
.tpz-event {
  display: flex; align-items: center; gap: 5px;
  padding: 4px 9px; border-radius: 20px; background: var(--tpz-panel-solid);
  font-size: 11px; font-weight: 800; letter-spacing: 0.05em; white-space: nowrap;
  transform: translate(-50%, -50%);
  border: 1px solid var(--tpz-border);
}
.tpz-hud:not(.tpz-reduced) .tpz-event { transition: opacity 0.3s ease; }
.tpz-event-arrow { font-size: 13px; line-height: 1; }

/* --- off-screen CHUTE accumulation chevrons (PLAYTEST F3, LOCKED-DESIGN 18.19)
   Reuses the .tpz-event chevron vocabulary; persistent rather than transient,
   so it carries a level tint and (level 3 only, flash permitting) a gentle
   pulse. Simple scales it ~1.4x — the same ratio the kid score/timer use. --- */
.tpz-chute { opacity: 1; }
.tpz-hud:not(.tpz-reduced) .tpz-chute.tpz-chute-full { animation: tpz-chute-pulse 0.9s ease-in-out infinite; }
@keyframes tpz-chute-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.62; } }
.tpz-hud.tpz-simple .tpz-chute { font-size: 15px; padding: 6px 13px; border-radius: 26px; border-width: 2px; }
.tpz-hud.tpz-simple .tpz-chute .tpz-event-arrow { font-size: 19px; }

/* per-chute intake pips inside the BINS meter — DECORATION inside existing
   chrome, deliberately without data-hud-element (see HudReadability) */
.tpz-chute-pips { display: flex; gap: 10px; margin-top: 5px; align-items: center; }
.tpz-chute-pip { display: flex; gap: 3px; align-items: center; line-height: 1; }
.tpz-chute-pip-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--tpz-text-dim); opacity: 0.32; }
.tpz-hud:not(.tpz-reduced) .tpz-chute-pip-dot { transition: background 0.25s ease, opacity 0.25s ease; }
.tpz-chute-pip-key { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; color: var(--tpz-text-dim); }
.tpz-chute-pip-val { font-size: 10px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--tpz-text-mid); }
/* Simple: bigger dots, no letters, no numerals (early-reader, 18.11) */
.tpz-hud.tpz-simple .tpz-chute-pips { gap: 16px; margin-top: 8px; justify-content: center; }
.tpz-hud.tpz-simple .tpz-chute-pip-dot { width: 16px; height: 16px; }
.tpz-hud.tpz-simple .tpz-chute-pip-key,
.tpz-hud.tpz-simple .tpz-chute-pip-val { display: none; }

/* --- world-anchored micro-labels (jam countdown, STALE route) --- */
.tpz-world {
  transform: translate(-50%, -50%);
  font-size: 12px; font-weight: 800; letter-spacing: 0.06em;
  padding: 2px 7px; border-radius: 4px; background: var(--tpz-panel-solid);
  white-space: nowrap;
}
/* optional linear progress gauge inside a world tag (lever-pull cue, 18.10) */
.tpz-world-gauge { height: 4px; margin-top: 3px; border-radius: 2px; background: rgba(255, 255, 255, 0.16); overflow: hidden; }
.tpz-world-gauge-fill { height: 100%; width: 0%; border-radius: 2px; background: currentColor; }

/* --- control-hint strip --- */
.tpz-hint { left: 12px; bottom: 10px; max-width: 720px; color: var(--tpz-text-dim); font-size: 12px; }
.tpz-fps { color: #5d6675; font-size: 11px; margin-left: 8px; }

/* --- full-frame effect layers (NOT chrome; carry no data-hud-element) --- */
.tpz-flash { position: absolute; inset: 0; pointer-events: none; opacity: 0; background: #fff; }
.tpz-vignette { position: absolute; inset: 0; pointer-events: none; display: none; }

/* --- terminal fault curtain (physics-core failure; NOT chrome) --- */
.tpz-fatal {
  position: absolute; inset: 0; display: none; z-index: 40;
  align-items: center; justify-content: center;
  background: rgba(4, 6, 10, 0.86);
}
.tpz-fatal-card {
  max-width: 460px; padding: 20px 26px; text-align: center;
  background: var(--tpz-panel-solid); border: 1px solid var(--tpz-red);
  border-radius: 8px; box-shadow: 0 0 34px rgba(0, 0, 0, 0.7);
}
.tpz-fatal-title {
  font-size: 20px; font-weight: 900; letter-spacing: 0.1em; color: var(--tpz-red);
  margin-bottom: 10px;
}
.tpz-fatal-body { font-size: 13px; color: var(--tpz-text-mid); }

/* --- floating score popups --- */
.tpz-popup {
  position: absolute; transform: translate(-50%, -50%);
  font-size: 20px; font-weight: 700; text-shadow: 0 0 12px currentColor;
  pointer-events: none; opacity: 1;
}
.tpz-hud:not(.tpz-reduced) .tpz-popup { transition: all 0.7s cubic-bezier(0.2, 0.7, 0.3, 1); }

/* --- Simple kid mode (LOCKED-DESIGN 18.11): bigger to see, less to read.
   Score ≈1.5× and timer ≈1.4×; the overflow meter is reframed as a bigger,
   friendlier BINS gauge. All sizing rides the same tokens. --- */
.tpz-hud.tpz-simple .tpz-score-value { font-size: 68px; }
.tpz-hud.tpz-simple .tpz-timer { font-size: 31px; }
/* keep score clear of the taller timer so the two never overlap */
.tpz-hud.tpz-simple .tpz-score { top: 48px; }
.tpz-hud.tpz-simple .tpz-overflow { width: 300px; }
.tpz-hud.tpz-simple .tpz-overflow-label { font-size: 14px; letter-spacing: 0.12em; }
.tpz-hud.tpz-simple .tpz-overflow-count { font-size: 17px; }
.tpz-hud.tpz-simple .tpz-overflow-track { height: 22px; border-radius: 6px; }
.tpz-bins-state {
  margin-top: 6px; text-align: center;
  font-size: 15px; font-weight: 800; letter-spacing: 0.06em;
  color: var(--tpz-amber);
}
.tpz-bins-state.tpz-severe { color: var(--tpz-red); }
.tpz-hud.tpz-simple .tpz-hint { font-size: 15px; }
/* placement-countdown center overlay ("GET READY… 5" → "GO!") */
.tpz-countdown {
  top: 40%; left: 50%; transform: translateX(-50%); text-align: center;
  font-size: 84px; font-weight: 900; letter-spacing: 0.03em; line-height: 1;
  color: var(--tpz-text-hi); text-shadow: 0 3px 18px #000;
}
.tpz-countdown.tpz-go { color: var(--tpz-teal); }
/* chain tier-up star-burst word ("GOOD!" … "MEGA!") */
.tpz-chainword {
  top: 30%; left: 50%; transform: translateX(-50%); text-align: center;
  font-size: 60px; font-weight: 900; letter-spacing: 0.05em; line-height: 1;
  text-shadow: 0 0 26px currentColor, 0 3px 14px #000;
}

/* --- Simple v2 auto-portal telegraph (LOCKED-DESIGN 18.11) --- */
.tpz-portal-banner {
  top: 15%; left: 50%; transform: translateX(-50%); text-align: center;
  color: ${ANCHOR_BLUE}; text-shadow: 0 0 22px ${ANCHOR_BLUE}, 0 3px 14px #000;
}
.tpz-portal-banner-line { font-size: 34px; font-weight: 900; letter-spacing: 0.04em; line-height: 1.1; }
.tpz-portal-banner-count { font-size: 66px; font-weight: 900; line-height: 1; margin-top: 4px; }
/* <=2Hz gentle pulse; disabled under prefers-reduced-motion / the flash knob */
.tpz-hud:not(.tpz-reduced) .tpz-portal-banner { animation: tpz-portal-pulse 0.5s ease-in-out infinite; }
.tpz-portal-beacon {
  transform: translate(-50%, -50%); text-align: center; pointer-events: none;
  width: 84px; height: 84px; border-radius: 50%;
  border: 4px solid ${ANCHOR_BLUE}; box-shadow: 0 0 20px ${ANCHOR_BLUE}, inset 0 0 16px ${ANCHOR_BLUE};
}
.tpz-hud:not(.tpz-reduced) .tpz-portal-beacon { animation: tpz-portal-pulse 0.5s ease-in-out infinite; }
.tpz-portal-beacon-arrow {
  position: absolute; left: 50%; top: -26px; transform: translateX(-50%);
  font-size: 26px; line-height: 1; color: ${ANCHOR_BLUE}; text-shadow: 0 0 12px ${ANCHOR_BLUE};
}
.tpz-portal-beacon-label {
  position: absolute; left: 50%; top: calc(100% + 6px); transform: translateX(-50%); white-space: nowrap;
  padding: 4px 8px; border-radius: 6px; background: var(--tpz-panel-solid);
  color: var(--tpz-text-hi); font-weight: 900; font-size: 13px; letter-spacing: 0.03em;
}
/* Simple's 0.2 pulse cap (18.11): opacity swing stays shallow, no full flash. */
@keyframes tpz-portal-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.8; } }
`;
}
